<template>
  <OfferTile
    :offer="offer"
    :tag-title="tagTitle"
    link-tag="nuxt-link"
    :rectangular-badge-hex="leftFlagColor"
    :class="{ 'offer-tile--treueShop': hasTreueshopFlag, 'offer-tile--discounted': !!discount }"
  >
    <template #image>
      <GoliathImageElement
        v-if="imageSources"
        :alt="title"
        :image="imageSources"
        :lazy="lazy"
        xs-width="small"
        sm-width="medium"
      />
      <div
        v-else
        class="offer-tile__image-404"
      />
    </template>
    <template
      v-if="rebateDescription"
      #rectangularBadge
    >
      AKTION
    </template>
    <template
      v-else-if="offer.isNew"
      #rectangularBadge
    >
      NEU
    </template>
    <template
      v-else-if="!!discount"
      #rectangularBadge
    >
      %
    </template>
    <template
      v-else-if="hasHighlightFlag"
      #rectangularBadge
    >
      Bestseller
    </template>

    <template
      v-if="hasTreueshopFlag"
      #highlightFlag
    >
      {{ treueShopFlagText }}
    </template>
    <template
      v-else-if="hasZeroPercentFinancing"
      #highlightFlag
    >
      <button
        ref="zeroPercentFinancingInfoLink"
        class="offer-tile__zero-percent-financing"
        data-tealium-rel="content.button.zero-percent-financing-legal"
        @click.prevent="openFinancingModal()"
      >
        0% Finanzierung<sup>*</sup>
      </button>
    </template>
    <template #brand>
      <CdnImage
        v-if="brand?.logo"
        :class="['offer-tile__logo-image', `offer-tile__logo-image--${brand.slug}`]"
        render-without-figure
        :src="brand.logo"
        :alt="`${brand.name} Logo`"
        :image-style="`aspect-ratio: ${brand.aspectRatio}`"
        :lazy="lazy"
      />
      <span
        v-else-if="brand"
        class="offer-tile__brand-placeholder"
        >{{ brand.name }}</span
      >
    </template>

    <template
      v-if="telekomContractLink"
      #offerContract
    >
      <div class="offer-tile__contract-wrapper">
        <LinkButton
          class="offer-tile__contract-link"
          tag="button"
          text
          @click.native.prevent="clickContractLink"
        >
          {{ contractLinkText }}
          <span class="sr-only">(Der Link wird in einem neuen Tab geöffnet)</span>
          <SvgIcon
            :image="externalIcon"
            aria-hidden="true"
          />
        </LinkButton>
      </div>
    </template>
    <template #energyLabel>
      <EnergyLabelContainer
        v-if="hasEnergyLabel"
        class="offer-tile__energy-label"
        :product="offer.product"
        datasheet-alignment="justify"
      />
    </template>
    <template #alternatives>
      <div
        v-if="isBundle"
        class="offer-tile__variants"
      >
        <AlternativeOffersCount :product-alternatives="offer.productAlternatives" />
      </div>
    </template>
    <template #info>
      <div
        v-if="inStock"
        class="offer-tile__delivery"
      >
        <span v-if="availability.deliveryTime">
          Lieferzeit: <span class="text text--nowrap">{{ availability.deliveryTime }}</span>
        </span>
      </div>
      <div
        v-else-if="availability"
        class="offer-tile__unavailable"
      >
        {{ availability.text }}
      </div>
    </template>
    <template #button>
      <div
        v-if="hasCartButton"
        class="offer-tile__add-to-cart"
      >
        <AddToCartButtonContainer
          display-cart-quantity
          :display-text="false"
          :offer="offer"
        />
      </div>
      <client-only>
        <LinkButton
          v-if="isBookmarkingAvailable"
          class="offer-tile__bookmark"
          :class="{ 'offer-tile__bookmark--active': isBookmarked }"
          tag="button"
          transparent
          aria-label="Produkt zur Merkliste hinzufügen"
          @click.native.prevent="toggleBookmarkNumber"
        >
          <svg-icon
            no-icon-class
            class="offer-tile__bookmark-icon"
            :image="IconBookmark"
          />
        </LinkButton>
      </client-only>
    </template>
    <template
      v-if="isMzaCompatible || isMshCompatible"
      #contentAfter
    >
      <div class="offer-tile__app-icons">
        <span
          v-if="isMzaCompatible"
          class="offer-tile__svg-icon-wrapper"
          title="MagentaZuhause App"
        >
          <SvgIcon
            :image="mzaIcon"
            class="offer-tile__mza-icon"
          />
        </span>
        <span
          v-if="isMshCompatible"
          class="offer-tile__svg-icon-wrapper"
          title="Magenta SmartHome App"
        >
          <SvgIcon :image="mshIcon" />
        </span>
      </div>
    </template>
  </OfferTile>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { getTelekomContractLink, getTelekomContractLinkText } from '@/lib/telekom-contract-link';
import {
  OfferTile,
  GoliathImageElement,
  LinkButton,
  calculateAvailability,
} from '@i22-td-smarthome/component-library';
import { getDiscount, getPrice, getEnergyEfficiencyProduct } from '@i22/rocket';
import Tealium from '@/lib/tealium';
import CdnImage from '@/components/cdn-image.vue';

import AlternativeOffersCount from '@/components/alternative-offers-count.vue';
import EnergyLabelContainer from '@/components/energy-label-container.vue';
import AddToCartButtonContainer from '~/components/product-detail/add-to-cart-button-container.vue';

import { mapGetters } from 'vuex';
import { getBrand } from '@/lib/brand';
import { getMainImageFromOffer } from '@/lib/goliath-media';

import IconBookmark from '@/assets/images/icon-bookmark.svg';
import useProductBookmarks from '@/composables/product-bookmarks';
import mzaIcon from '@/assets/images/mza-logo-magenta-solid.svg';
import mshIcon from '@/assets/images/msh-logo.svg';
import externalIcon from '@/assets/images/external-link.svg';

const PROVIDER_NAMES = {
  BRODOS: 'Brodos AG',
  TELEKOM: 'Telekom Deutschland GmbH',
  TVPP: 'Telekom Deutschland GmbH',
};

const HIGHLIGHTS_OFFER = [
  'DCS8302LH-SPK',
  '4251281645534',
  '8718699787059',
  '4251281641420',
  '4251281645138',
];

export default {
  name: 'OfferTileContainer',
  setup() {
    const { isProductInBookmarks, isBookmarkingAvailable, productBookmarks, toggleBookmark } =
      useProductBookmarks();
    return { isProductInBookmarks, isBookmarkingAvailable, productBookmarks, toggleBookmark };
  },
  components: {
    AddToCartButtonContainer,
    OfferTile,
    AlternativeOffersCount,
    GoliathImageElement,
    EnergyLabelContainer,
    LinkButton,
    CdnImage,
  },
  props: {
    tagTitle: { type: String, default: 'h2' },
    offer: { type: Object, required: true },
    hasCartButton: { type: Boolean },
    lazy: { type: Boolean, default: false },
  },
  data() {
    return {
      IconBookmark,
      mshIcon,
      mzaIcon,
      externalIcon,
    };
  },
  computed: {
    ...mapGetters('coupon', [
      'couponInfo',
      'isValidCoupon',
      'incompatibleArticleNumbers',
      'compatibleArticleNumbers',
      'compatibleProductProviders',
      'reductionValue',
    ]),
    ...mapGetters('offers', ['rebates', 'zeroPercentFinancing']),
    isBookmarked() {
      return this.isProductInBookmarks(this.offer?.product?.articleNumber);
    },
    availability() {
      return calculateAvailability(
        this.offer.product?.availability,
        this.offer.product?.alwaysDisplayed
      );
    },
    title() {
      if (this.offer.contract) return this.offer.contract.name;
      return this.offer.product.family.name;
    },
    isBundle() {
      return !!(this.offer.product && this.offer.product.bundle);
    },
    rebateDescription() {
      return this.rebates[this.offer.product.articleNumber]?.description;
    },
    deliveryLeadTime() {
      if (this.offer.contract) return '';
      return this.offer.product.deliveryLeadTime;
    },
    imageSources() {
      return getMainImageFromOffer(this.offer);
    },
    brand() {
      return getBrand(this.offer);
    },
    productTags() {
      if (!this.offer?.product) return [];
      let tags = [];
      if (this.offer.product.bundledProducts?.length > 0) {
        const { bundledProducts } = this.offer.product;

        tags = bundledProducts[0].tags?.filter((tag) =>
          bundledProducts.every((product) => product.tags?.includes(tag))
        );
      } else if (this.offer.product.tags) {
        tags = this.offer.product.tags;
      }
      return tags?.map((str) => str.toLowerCase()) || [];
    },
    isMzaCompatible() {
      return ['mzabasic', 'mzapro'].some((tag) => this.productTags.includes(tag));
    },
    isMshCompatible() {
      return ['mshfree', 'mshpro'].some((tag) => this.productTags.includes(tag));
    },
    inStock() {
      const { product } = this.offer;
      // An offer must always contain at least a product or contract (or both).
      // When no product is present in the offer we can be certain to have a
      // contract. As contracts are intangible they may not have stock and are
      // considered always available.
      if (!product) return true;
      return product.availability?.stock > 0;
    },
    providerName() {
      const { product } = this.offer;
      if (this.offer.contract) return PROVIDER_NAMES[this.offer.contract.provider];
      return PROVIDER_NAMES[product.availability.provider];
    },
    price() {
      return getPrice(this.offer.prices, 'DEFAULT', 'ONETIME')?.value;
    },
    wasPrice() {
      return getPrice(this.offer.prices, 'WAS', 'ONETIME')?.value;
    },
    hasHighlightFlag() {
      return this.articleNumbersWithAlternatives.some((articleNumber) =>
        HIGHLIGHTS_OFFER.includes(articleNumber)
      );
    },
    articleNumbersWithAlternatives() {
      const articleNumbers = [this.offer?.product?.articleNumber];
      const properties = this.offer.productAlternatives?.[0]?.properties || [];

      properties.forEach(({ alternatives }) => {
        articleNumbers.push(
          ...alternatives
            .map(({ offer }) => offer?.product?.articleNumber)
            .filter((articleNumber) => articleNumber && !articleNumbers.includes(articleNumber))
        );
      });

      return articleNumbers.filter(Boolean);
    },
    discount() {
      return getDiscount(this.price, this.wasPrice);
    },
    telekomContractLink() {
      return this.offer.product?.availability?.provider === 'TELEKOM'
        ? getTelekomContractLink(this.offer)
        : '';
    },
    contractLinkText() {
      return getTelekomContractLinkText(this.$route.path);
    },
    isCouponCompatible() {
      if (this.compatibleArticleNumbers?.length > 0) {
        return this.compatibleArticleNumbers.includes(this.offer.product.articleNumber);
      }
      if (this.compatibleProductProviders?.length > 0) {
        return this.compatibleProductProviders.includes(this.offer.product.availability.provider);
      }
      if (this.incompatibleArticleNumbers?.length > 0) {
        return !this.incompatibleArticleNumbers.includes(this.offer.product.articleNumber);
      }
      return true;
    },
    hasZeroPercentFinancing() {
      return this.zeroPercentFinancing.includes(this.offer.product.articleNumber);
    },
    hasTreueshopFlag() {
      return (
        this.isValidCoupon &&
        this.couponInfo.type === 'coupon' &&
        this.reductionValue &&
        this.couponInfo.typeCode === '02' &&
        this.isCouponCompatible &&
        !this.offer.prices?.some((price) => price.type.toLowerCase() === 'was')
      );
    },
    treueShopFlagText() {
      if (this.couponInfo.reductionMethodCode === 'RP') return `-${this.reductionValue}% Rabatt`;
      if (['RS', 'RT'].includes(this.couponInfo.reductionMethodCode))
        return `abzüglich ${this.reductionValue} €`;
      return '';
    },
    leftFlagColor() {
      if (this.offer.isNew || !!this.discount || this.rebateDescription || !this.hasHighlightFlag)
        return '317CB3';
      return 'e20074';
    },
    hasEnergyLabel() {
      return !!getEnergyEfficiencyProduct(this.offer.product);
    },
  },
  methods: {
    toggleBookmarkNumber() {
      this.toggleBookmark(
        this.offer?.product?.articleNumber,
        this.offer?.product?.name,
        this.price
      );
    },
    clickContractLink() {
      Tealium.link({
        wt_link_id: `content.button.zu-den-details.${this.offer?.product?.articleNumber}`,
      });
      window.open(this.telekomContractLink, '_blank');
    },
    openFinancingModal() {
      this.$modal.open({
        component: {
          is: defineAsyncComponent(() => import('~/components/zero-percent-modal-content.vue')),
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.offer-tile {
  --max-list-width: 90vw;

  &__bookmark,
  &__add-to-cart {
    position: relative;
    margin-top: -1rem;

    @include mq(false, $mq-xl - 1) {
      bottom: 1rem;
    }
  }

  &__bookmark {
    margin-right: $grid-base * -1;
    border-right: none;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    padding: 5px 16px;

    @include mq($mq-xl) {
      margin-right: $grid-base * -2;
    }

    &-icon {
      fill: none;
      stroke: currentColor;
      stroke-width: 1px;
      width: 24px;
    }

    &--active {
      color: var(--color-primary);
      border-color: var(--color-primary);

      .offer-tile__bookmark-icon {
        fill: currentColor;
      }
    }
  }

  &__zero-percent-financing {
    appearance: none;
    background: transparent;
    border: 0;
    margin: 0;
    padding: 0;
    color: white;
    font-weight: 700;
    cursor: pointer;
    @include font-size(14px, 14px);

    sup {
      @include font-size(18px, 0px);
      line-height: 0;
      vertical-align: baseline;
    }

    &:hover,
    &:active,
    &:visited,
    &:focus {
      color: white;
    }
  }

  &__delivery,
  &__unavailable {
    flex: 1;
  }

  &__unavailable {
    color: var(--color-error);
  }

  &__contract-link {
    text-align: left;
    @include font-size(16px, 18px);
    white-space: normal;
    padding-block: 3px;
  }

  &__logo-image {
    height: 100%;
    align-self: flex-start;
    object-fit: contain;
  }

  &__brand-placeholder {
    @include font-size(15px, 18px);
  }

  &--treueShop {
    :deep(.offer-tile__highlight-flag) {
      background-color: color(blue, 2000);
    }
  }

  :deep(.offer-tile__info) {
    justify-content: space-between;
  }

  :deep(.offer-tile__content) {
    min-height: 40px; // space for the bookmark button
  }

  &--discounted {
    :deep(.offer-tile__rectangular-badge) {
      --badge-font-size: 21px;
    }
  }

  &__rectangular-badge,
  &__highlight-flag {
    @include mq($mq-sm, $mq-lg - 1) {
      --flag-padding: 12px;
    }
  }

  &__image-404 {
    display: block;
    aspect-ratio: 1;
    object-fit: contain;
    background: linear-gradient(135deg, #ececec 0%, #ffffff 100%);
    height: var(--image-height);
  }

  &__svg-icon-wrapper {
    svg {
      height: 2 * $grid-base;
      width: auto;
    }
  }

  &__app-icons {
    display: flex;
    justify-content: flex-start;
    align-self: flex-end;
    gap: 0.5 * $grid-base;
    height: 2 * $grid-base;
    grid-column: 1 / 3;
    grid-row: 9;
    @include mq(false, $mq-xl - 1) {
      margin-bottom: $grid-base;
    }
  }
}
</style>
